// --- FINAL FILE: packages/api-client/src/services/admin/adminProjectService.ts ---

import { getApiClient } from '../../client';
import { handleApiError } from '../../utils/handleApiError';
import type { 
    ServiceResponse,
    Project,
    ProjectVersion,
    UUID,
    RevertPayload,
    AdminProjectListItem,
} from '@dopomogai/types';

const ADMIN_PROJECTS_PREFIX = '/admin/projects';

export const adminProjectService = {
  // =================================================================
  // ==                      PROJECT ENTITY CRUD                    ==
  // =================================================================

  /** Creates a new project. The payload can now include a status. */
  createProject: (data: Omit<Project, 'id' | 'version' | 'created_at' | 'updated_at'>): ServiceResponse<Project> => {
    return getApiClient().post<Project>(ADMIN_PROJECTS_PREFIX, data)
      .then(res => res.data)
      .catch(err => handleApiError(err, `creating project '${data.name}'`));
  },

  /** 
   * Lists all available projects for the admin dashboard.
   */
  listProjects: (): ServiceResponse<AdminProjectListItem[]> => {
    return getApiClient().get<AdminProjectListItem[]>(ADMIN_PROJECTS_PREFIX)
      .then(res => res.data)
      .catch(err => handleApiError(err, 'listing all projects'));
  },

  /** Updates an existing project's core details. */
  updateProject: (projectId: UUID, data: Project): ServiceResponse<Project> => {
    return getApiClient().put<Project>(`${ADMIN_PROJECTS_PREFIX}/${projectId}`, data)
      .then(res => res.data)
      .catch(err => handleApiError(err, `updating project ${projectId}`));
  },

   /** Deletes an entire project and all its associated data. */
   deleteProject: (projectId: UUID): ServiceResponse<void> => {
    return getApiClient().delete<void>(`${ADMIN_PROJECTS_PREFIX}/${projectId}`)
      .then(res => res.data)
      .catch(err => handleApiError(err, `deleting project ${projectId}`));
  },

  // =================================================================
  // ==                 PROJECT VERSIONING & REVERT                 ==
  // =================================================================

  /** Retrieves the version history (commit log) for an entire project. */
  listProjectVersions: (projectId: UUID): ServiceResponse<ProjectVersion[]> => {
    return getApiClient().get<ProjectVersion[]>(`${ADMIN_PROJECTS_PREFIX}/${projectId}/versions`)
      .then(res => res.data)
      .catch(err => handleApiError(err, `listing versions for project ${projectId}`));
  },
  
  /** Reverts the project's core details to a previous version's state. */
  revertProject: (projectId: UUID, payload: RevertPayload): ServiceResponse<Project> => {
    return getApiClient().post<Project>(`${ADMIN_PROJECTS_PREFIX}/${projectId}/revert`, payload)
      .then(res => res.data)
      .catch(err => handleApiError(err, `reverting project ${projectId} to version ${payload.version}`));
  },
};